import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { BulkInvitationRowValues, readText } from "legal-entities/invitations/bulk/bulk-invitation-column-spec";
import {
  BulkInvitationAPIServiceError,
  TeamLeaderRosterRow,
  useInviteTeamLeadersMutation,
} from "legal-entities/invitations/bulk/_api/bulk-invitation-api-service";
import { readApiError } from "_shared/_errors/api-error";
import BulkInvitationGrid from "legal-entities/invitations/bulk/_views/bulk-invitation-grid";
import teamLeaderRosterColumns from "legal-entities/invitations/bulk/team-leader-roster-columns";
import { useGetOnboardingWorkTemplateCatalogQuery } from "legal-entities/agreements/_api/entity-agreement-api-service";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { useMemo, useState } from "react";
import SearchUsersAPIRequest from "users/super-users/_api/commands/search-users-api-request";
import {
  ClientRepresentativeChoice,
  useSearchClientRepresentativesQuery,
} from "users/super-users/_api/super-user-api";

const PageContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: theme.spacing(1),
  overflow: "auto",
  padding: theme.spacing(2),
}));
const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

/** Below this a search matches most of the directory, which is not a list anybody can choose from. */
const MINIMUM_SEARCH_LENGTH = 2;

function toRow(values: BulkInvitationRowValues): TeamLeaderRosterRow {
  const message = readText(values, "message");
  return {
    email: readText(values, "email"),
    firstName: readText(values, "firstName"),
    lastName: readText(values, "lastName"),
    title: readText(values, "title"),
    entityName: readText(values, "entityName"),
    entityLocation: {
      address1: readText(values, "address1"),
      city: readText(values, "city"),
      state: readText(values, "state"),
      postalCode: readText(values, "postalCode"),
      country: readText(values, "country"),
    },
    message: message.length > 0 ? message : undefined,
  };
}

function describeRepresentative(person: ClientRepresentativeChoice): string {
  const company = person.companyName ? ` - ${person.companyName}` : "";
  return `${person.firstName} ${person.lastName}${company}`;
}

/**
 * A client's whole list of firms, entered once by whoever is running the roll-out.
 *
 * The sponsoring client is chosen by naming the person at that client who is copied on the
 * letters, rather than by picking an organization and then a person in it. The pair travels
 * together in the API and is only useful whole, and the representative already carries the legal
 * entity they belong to - so one choice answers both halves, and they cannot be made to disagree.
 */
export default function TeamLeaderRosterPage() {
  const user = useSessionUser();
  const [search, setSearch] = useState("");
  const [representative, setRepresentative] = useState<ClientRepresentativeChoice | null>(null);
  const [hasChosenRepresentative, setHasChosenRepresentative] = useState(false);
  const [unavailableRepresentative, setUnavailableRepresentative] = useState<{
    userId: string;
    legalEntityId: string;
  } | null>(null);
  const [onboardingTemplateId, setOnboardingTemplateId] = useState("");
  const [inviteTeamLeaders] = useInviteTeamLeadersMutation();

  const templatesQuery = useGetOnboardingWorkTemplateCatalogQuery();
  // The same request on every render: RTK Query compares a class instance by reference, so one built
  // during render would send a failed search again on every render (#6488).
  const peopleRequest = useMemo(() => new SearchUsersAPIRequest(search, 0, 20), [search]);
  const peopleQuery = useSearchClientRepresentativesQuery(
    { request: peopleRequest },
    { skip: search.trim().length < MINIMUM_SEARCH_LENGTH },
  );

  if (!user) return <Loader text="Loading..." />;
  if (!user.canAdministerPlatform()) return <NotAuthorized />;

  const people = peopleQuery.data?.data ?? [];
  const templates = templatesQuery.data ?? [];

  return (
    <PageContainer>
      <PageTitle variant="h5">Invite a client&apos;s team leaders</PageTitle>
      <Typography variant="body2" sx={{ mb: 2, maxWidth: "48rem" }}>
        One row per firm the client wants to keep working with. Each row opens a roll-out and sends
        that firm&apos;s team leader an invitation naming the client.
      </Typography>

      <Stack spacing={2} sx={{ mb: 3, maxWidth: "48rem" }}>
        <Autocomplete
          options={people}
          value={representative}
          onChange={(_event, chosen) => {
            setRepresentative(chosen);
            if (chosen) {
              setHasChosenRepresentative(true);
              if (
                chosen.userId.value !== unavailableRepresentative?.userId ||
                chosen.legalEntityId.value !== unavailableRepresentative.legalEntityId
              ) {
                setUnavailableRepresentative(null);
              }
            }
          }}
          onInputChange={(_event, text) => setSearch(text)}
          getOptionLabel={describeRepresentative}
          // The search is the server's, so what it returned is the answer. Material filters the
          // options it is given against the typed text by default, which here filters a second
          // time against a label the search did not match on: the directory matches an email
          // address and the label carries a name, so typing the one thing staff reliably know
          // about a client's representative showed "No options". Every other server-backed
          // Autocomplete in the app passes this for the same reason.
          filterOptions={(options) => options}
          isOptionEqualToValue={(option, value) =>
            option.userId.value === value.userId.value &&
            option.legalEntityId.value === value.legalEntityId.value
          }
          loading={peopleQuery.isFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client representative"
              helperText="The person at the client who is copied on every letter. Their organization is the sponsoring client."
            />
          )}
        />

        <TextField
          select
          label="Onboarding template"
          value={onboardingTemplateId}
          onChange={(event) => setOnboardingTemplateId(event.target.value)}
          helperText="Applied to every firm on the list. Leave blank for the default."
          slotProps={{ inputLabel: { shrink: true } }}
        >
          <MenuItem value="">Default</MenuItem>
          {templates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {template.displayName}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {hasChosenRepresentative ? (
        <BulkInvitationGrid
          spec={teamLeaderRosterColumns}
          submitLabel="Invite these firms"
          description={representative
            ? `Every firm below is invited on behalf of ${describeRepresentative(representative)}.`
            : "Choose a client representative to send this list."}
          submitBlockedReason={!representative
            ? "Choose a client representative before sending the list."
            : representative.userId.value === unavailableRepresentative?.userId &&
                representative.legalEntityId.value === unavailableRepresentative.legalEntityId
              ? "This representative is no longer available for the client. Choose another representative."
              : undefined}
          onSubmit={async (rows) => {
            if (!representative) throw new Error("A client representative is required.");
            const selectedOnboardingTemplateId = onboardingTemplateId.length > 0
              ? onboardingTemplateId
              : undefined;
            try {
              return await inviteTeamLeaders({
                sponsoringClientLegalEntityId: representative.legalEntityId,
                sponsoringClientRepresentativeUserId: representative.userId,
                onboardingTemplateId: selectedOnboardingTemplateId,
                rows: rows.map(toRow),
              }).unwrap();
            } catch (error) {
              const rawError = error instanceof BulkInvitationAPIServiceError ? error.error : error;
              if (readApiError(rawError).code === "client_representative_unavailable") {
                setUnavailableRepresentative({
                  userId: representative.userId.value,
                  legalEntityId: representative.legalEntityId.value,
                });
                if (search.trim().length >= MINIMUM_SEARCH_LENGTH) {
                  try {
                    await peopleQuery.refetch();
                  } catch {
                    // Keep the original stale-selection response visible if refreshing fails.
                  }
                }
              }
              throw error;
            }
          }}
        />
      ) : (
        <Alert severity="info">
          Choose the client representative first. Every letter names their organization as the
          client, so the list cannot be entered until it is known which client it is for.
        </Alert>
      )}
    </PageContainer>
  );
}
